/**
 * VGrove 守卫失败提示组件系统
 * 支持多种显示方式和动画效果的提示组件
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, Shield, Lock } from 'lucide-react';
import type { GuardFailureConfig } from '../client/middleware';
import { debug } from '../client';
/**
 * 提示项
 */
interface NotificationItem {
  id: string;
  config: GuardFailureConfig;
  timestamp: number;
}

/**
 * 上下文类型
 */
interface GuardFailureContextType {
  /** 显示提示 */
  showNotification: (config: GuardFailureConfig) => string;
  /** 关闭提示 */
  closeNotification: (id: string) => void;
  /** 清空所有提示 */
  clearAll: () => void;
  /** 当前提示列表 */
  notifications: NotificationItem[];
}

const GuardFailureContext = createContext<GuardFailureContextType | null>(null);

/**
 * 动画样式映射
 */
const animationClasses = {
  fade: {
    enter: 'opacity-0',
    enterActive: 'opacity-100 transition-opacity',
    exit: 'opacity-100',
    exitActive: 'opacity-0 transition-opacity'
  },
  slide: {
    enter: 'transform translate-x-full',
    enterActive: 'transform translate-x-0 transition-transform',
    exit: 'transform translate-x-0',
    exitActive: 'transform translate-x-full transition-transform'
  },
  bounce: {
    enter: 'transform scale-50 opacity-0',
    enterActive: 'transform scale-100 opacity-100 transition-all duration-300 ease-out',
    exit: 'transform scale-100 opacity-100',
    exitActive: 'transform scale-95 opacity-0 transition-all duration-200 ease-in'
  },
  shake: {
    enter: 'transform translate-x-0',
    enterActive: 'animate-pulse transition-all',
    exit: 'transform translate-x-0',
    exitActive: 'transform translate-x-full transition-transform'
  }
};

/**
 * 图标组件
 */
const NotificationIcon: React.FC<{ type?: string; customIcon?: React.ReactNode }> = ({
  type = 'warning',
  customIcon
}) => {
  if (customIcon) {
    return <>{customIcon}</>;
  }

  const iconProps = { className: 'w-5 h-5' };

  switch (type) {
    case 'shield':
      return <Shield {...iconProps} />;
    case 'lock':
      return <Lock {...iconProps} />;
    case 'warning':
    default:
      return <AlertTriangle {...iconProps} />;
  }
};

/**
 * Toast 通知组件
 */
const ToastNotification: React.FC<{
  item: NotificationItem;
  onClose: (id: string) => void;
}> = ({ item, onClose }) => {
  const { config } = item;
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const animation = animationClasses[config.animation || 'fade'];
  const duration = config.duration || 300;

  useEffect(() => {
    // 进入动画
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(item.id);
    }, duration);
  }, [item.id, onClose, duration]);

  useEffect(() => {
    // 自动关闭
    if (config.autoClose) {
      const timer = setTimeout(() => {
        handleClose();
      }, config.autoClose);
      return () => clearTimeout(timer);
    }
  }, [config.autoClose, handleClose]);

  const baseClasses = `
    fixed top-4 right-4 z-50 max-w-sm w-full
    bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800
    rounded-lg shadow-lg p-4
    ${config.className || ''}
  `;

  const animationClass = isExiting
    ? `${animation.exitActive} duration-${duration}`
    : isVisible
      ? `${animation.enterActive} duration-${duration}`
      : animation.enter;

  return (
    <div className={`${baseClasses} ${animationClass}`}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 text-red-500">
          <NotificationIcon type={config.icon} customIcon={config.customIcon} />
        </div>

        <div className="flex-1 min-w-0">
          {config.title && (
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              {config.title}
            </h4>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {config.message}
          </p>

          {config.actions && config.actions.length > 0 && (
            <div className="mt-3 flex space-x-2">
              {config.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`
                    px-3 py-1 text-xs font-medium rounded
                    ${action.variant === 'primary'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : action.variant === 'danger'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500'
                    }
                    transition-colors duration-200
                  `}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {config.closable !== false && (
          <button
            onClick={handleClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Modal 通知组件
 */
const ModalNotification: React.FC<{
  item: NotificationItem;
  onClose: (id: string) => void;
}> = ({ item, onClose }) => {
  const { config } = item;
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const animation = animationClasses[config.animation || 'fade'];
  const duration = config.duration || 300;

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(item.id);
    }, duration);
  }, [item.id, onClose, duration]);

  useEffect(() => {
    if (config.autoClose) {
      const timer = setTimeout(() => {
        handleClose();
      }, config.autoClose);
      return () => clearTimeout(timer);
    }
  }, [config.autoClose, handleClose]);

  const overlayClass = isExiting
    ? 'opacity-0'
    : isVisible
      ? 'opacity-100'
      : 'opacity-0';

  const modalClass = isExiting
    ? `${animation.exitActive} duration-${duration}`
    : isVisible
      ? `${animation.enterActive} duration-${duration}`
      : animation.enter;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4`}>
      {/* 背景遮罩 */}
      <div
        className={`absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-${duration} ${overlayClass}`}
        onClick={config.closable !== false ? handleClose : undefined}
      />

      {/* Modal 内容 */}
      <div className={`
        relative bg-white dark:bg-gray-800 rounded-lg shadow-xl
        max-w-md w-full p-6 ${config.className || ''} ${modalClass}
      `}>
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 text-red-500">
            <NotificationIcon type={config.icon} customIcon={config.customIcon} />
          </div>

          <div className="flex-1 min-w-0">
            {config.title && (
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {config.title}
              </h3>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              {config.message}
            </p>

            <div className="flex justify-end space-x-3">
              {config.actions && config.actions.length > 0 ? (
                config.actions.map((action, index) => (
                  <button
                    key={index}
                    onClick={action.onClick}
                    className={`
                      px-4 py-2 text-sm font-medium rounded-md
                      ${action.variant === 'primary'
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : action.variant === 'danger'
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500'
                      }
                      transition-colors duration-200
                    `}
                  >
                    {action.label}
                  </button>
                ))
              ) : (
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-800 bg-gray-200 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500 transition-colors duration-200"
                >
                  确定
                </button>
              )}
            </div>
          </div>

          {config.closable !== false && (
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Inline 通知组件
 */
const InlineNotification: React.FC<{
  item: NotificationItem;
  onClose: (id: string) => void;
}> = ({ item, onClose }) => {
  const { config } = item;
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const animation = animationClasses[config.animation || 'fade'];
  const duration = config.duration || 300;

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(item.id);
    }, duration);
  }, [item.id, onClose, duration]);

  useEffect(() => {
    if (config.autoClose) {
      const timer = setTimeout(() => {
        handleClose();
      }, config.autoClose);
      return () => clearTimeout(timer);
    }
  }, [config.autoClose, handleClose]);

  const animationClass = isExiting
    ? `${animation.exitActive} duration-${duration}`
    : isVisible
      ? `${animation.enterActive} duration-${duration}`
      : animation.enter;

  return (
    <div className={`
      bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
      rounded-md p-4 mb-4 ${config.className || ''} ${animationClass}
    `}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 text-red-500">
          <NotificationIcon type={config.icon} customIcon={config.customIcon} />
        </div>

        <div className="flex-1 min-w-0">
          {config.title && (
            <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
              {config.title}
            </h4>
          )}
          <p className="text-sm text-red-700 dark:text-red-300">
            {config.message}
          </p>

          {config.actions && config.actions.length > 0 && (
            <div className="mt-3 flex space-x-2">
              {config.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`
                    px-3 py-1 text-xs font-medium rounded
                    ${action.variant === 'primary'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : action.variant === 'danger'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-white text-red-800 hover:bg-red-50 dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700'
                    }
                    transition-colors duration-200
                  `}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {config.closable !== false && (
          <button
            onClick={handleClose}
            className="flex-shrink-0 text-red-400 hover:text-red-600 dark:hover:text-red-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * 守卫失败提示提供者组件
 */
export const GuardFailureProvider: React.FC<{
  children: React.ReactNode;
  maxNotifications?: number;
}> = ({ children, maxNotifications = 5 }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const showNotification = useCallback((config: GuardFailureConfig): string => {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const item: NotificationItem = {
      id,
      config,
      timestamp: Date.now()
    };

    setNotifications(prev => {
      const newNotifications = [item, ...prev];
      // 限制最大通知数量
      return newNotifications.slice(0, maxNotifications);
    });

    return id;
  }, [maxNotifications]);

  const closeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(item => item.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const contextValue: GuardFailureContextType = {
    showNotification,
    closeNotification,
    clearAll,
    notifications
  };

  // 渲染不同类型的通知
  const renderNotifications = () => {
    const toasts = notifications.filter(item => item.config.type === 'toast');
    const modals = notifications.filter(item => item.config.type === 'modal');
    const inlines = notifications.filter(item => item.config.type === 'inline');

    return (
      <>
        {/* Toast 通知 */}
        {toasts.map(item => (
          <ToastNotification
            key={item.id}
            item={item}
            onClose={closeNotification}
          />
        ))}

        {/* Modal 通知 */}
        {modals.map(item => (
          <ModalNotification
            key={item.id}
            item={item}
            onClose={closeNotification}
          />
        ))}

        {/* Inline 通知 */}
        <div className="vgrove-inline-notifications">
          {inlines.map(item => (
            <InlineNotification
              key={item.id}
              item={item}
              onClose={closeNotification}
            />
          ))}
        </div>
      </>
    );
  };

  return (
    <GuardFailureContext.Provider value={contextValue}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        renderNotifications(),
        document.body
      )}
    </GuardFailureContext.Provider>
  );
};

/**
 * 使用守卫失败提示的 Hook
 */
export const useGuardFailure = () => {
  const context = useContext(GuardFailureContext);
  if (!context) {
    throw new Error('useGuardFailure must be used within a GuardFailureProvider');
  }
  return context;
};

/**
 * 处理 HTTP 响应中的守卫失败信息
 */
export const handleGuardFailureResponse = (response: Response) => {
  const guardFailureHeader = response.headers.get('X-VGrove-Guard-Failure');

  if (guardFailureHeader) {
    try {
      const config: GuardFailureConfig = JSON.parse(guardFailureHeader);

      // 在客户端显示提示
      if (typeof window !== 'undefined') {
        const event = new CustomEvent('vgrove-guard-failure', {
          detail: config
        });
        window.dispatchEvent(event);
      }

      return config;
    } catch (error) {
      debug.error('解析守卫失败配置失败:', error);
    }
  }

  return null;
};

/**
 * 监听守卫失败事件的 Hook
 */
export const useGuardFailureListener = () => {
  const { showNotification } = useGuardFailure();

  useEffect(() => {
    const handleGuardFailure = (event: CustomEvent<GuardFailureConfig>) => {
      showNotification(event.detail);
    };

    window.addEventListener('vgrove-guard-failure', handleGuardFailure as EventListener);

    return () => {
      window.removeEventListener('vgrove-guard-failure', handleGuardFailure as EventListener);
    };
  }, [showNotification]);
};